import { Component, OnInit, ViewChild, Input, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SlickCarouselComponent } from 'ngx-slick-carousel';
import { FooterService } from 'src/app/core';
import { Errors } from 'src/app/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface SliderItem {
  id: string;
  content: string;
  safeContent?: SafeHtml;
  startDate?: string;
  endDate?: string;
}

interface UserCompany {
  region: string;
}

interface UserProfile {
  company: UserCompany;
}

interface CarouselConfig {
  slidesToShow: number;
  slidesToScroll: number;
  dots: boolean;
  arrows: boolean;
  speed: number;
  infinite: boolean;
  autoplay: boolean;
  autoplaySpeed: number;
  pauseOnHover: boolean;
  pauseOnFocus: boolean;
  pauseOnDotsHover: boolean;
  waitForAnim: boolean;
  swipe: boolean;
  draggable: boolean;
  touchMove: boolean;
}

@Component({
  selector: 'app-top-banner',
  templateUrl: './top-banner.component.html',
  styleUrls: ['./top-banner.component.scss']
})
export class TopBannerComponent implements OnInit, OnDestroy {

  @Input() userProfile!: UserProfile;
  @Input() switchRole: string | undefined;

  @ViewChild('slickModal1') slickModal1: SlickCarouselComponent | undefined;

  sliderData: SliderItem[] = [];
  errors: Errors = { errors: {} };

  private readonly destroy$ = new Subject<void>();
  private readonly CAROUSEL_CONFIG: CarouselConfig = {
    slidesToShow: 1,
    slidesToScroll: 1,
    dots: false,
    arrows: true,
    speed: 300,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: false,
    pauseOnFocus: false,
    pauseOnDotsHover: false,
    waitForAnim: false,
    swipe: true,
    draggable: true,
    touchMove: false
  };

  private readonly AUTOPLAY_RESUME_DELAY_MS = 30;
  private readonly DATE_SLICE_END_INDEX = 10;

  get slideConfig(): CarouselConfig {
    return this.CAROUSEL_CONFIG;
  }

  constructor(
    private sanitizer: DomSanitizer,
    private footerService: FooterService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadSliderImages();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSlideChanged(): void {
    if (this.slickModal1) {
      this.slickModal1.slickPlay();
    }
  }

  trackBySlide(index: number, item: SliderItem): string {
    return item.id || String(index);
  }

  handleSlideClick(event: Event): void {
    if (this.slickModal1) {
      this.slickModal1.slickPause();
    }

    const target = event.target as HTMLElement;
    if (!(target instanceof HTMLAnchorElement)) {
      return;
    }

    const href = target.getAttribute('href');
    const targetAttr = target.getAttribute('target');
    const isExternalLink = targetAttr === '_blank';

    if (!isExternalLink && href) {
      event.preventDefault();
      this.router.navigate([`/${href}`]);
    }
  }

  private loadSliderImages(): void {
    if (!this.userProfile || !this.userProfile.company || !this.userProfile.company.region) {
      return;
    }

    const queryParams = {
      region: this.userProfile.company.region,
      console: this.switchRole === 'manager' ? 'LEADER' : 'SELLER'
    };

    this.footerService.getSliderImages(queryParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: SliderItem[]) => this.processSliderData(data),
        error: (err: Errors) => this.handleError(err)
      });
  }

  private processSliderData(data: SliderItem[]): void {
    this.sliderData.length = 0;

    data.forEach((slide) => {
      if (this.isSlideValid(slide)) {
        slide.safeContent = this.sanitizer.bypassSecurityTrustHtml(slide.content);
        this.sliderData.push(slide);
      }
    });

    this.resumeAutoplay();
  }

  private isSlideValid(slide: SliderItem): boolean {
    if (!slide.startDate || !slide.endDate) {
      return true;
    }
    return this.isDateInRange(slide);
  }

  private isDateInRange(slide: SliderItem): boolean {
    if (!slide.startDate || !slide.endDate) {
      return true;
    }

    const today = this.formatDateAsString(new Date());
    const startDate = slide.startDate.substring(0, this.DATE_SLICE_END_INDEX);
    const endDate = slide.endDate.substring(0, this.DATE_SLICE_END_INDEX);

    return startDate <= today && today <= endDate;
  }

  private formatDateAsString(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  private resumeAutoplay(): void {
    setTimeout(() => {
      if (this.slickModal1) {
        this.slickModal1.slickPlay();
      }
    }, this.AUTOPLAY_RESUME_DELAY_MS);
  }

  private handleError(err: Errors): void {
    this.errors = err;
  }
}
